'use client';

import { Download } from '@mui/icons-material';
import { IconButton, Popover } from '@mui/material';
import {
  type ComponentProps,
  type FC,
  type MouseEvent,
  type ReactNode,
  useCallback,
  useMemo,
  useState,
} from 'react';
import { DownloadPdfForm } from './download-pdf-form';

type ButtonProps = {
  ariaDescribeBy?: string;
  onClick: (event: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
};

type Props = Omit<ComponentProps<typeof DownloadPdfForm>, 'onFinish'> & {
  children?: (props: ButtonProps) => ReactNode;
  disabled?: boolean;
};

export const DownloadPdfButton: FC<Props> = (props) => {
  const {
    users,
    organization = undefined,
    disabled = false,
    config,
    children = (props) => (
      <IconButton {...props} sx={{ opacity: props.disabled ? 0.5 : 1 }}>
        <Download color="gray" />
      </IconButton>
    ),
  } = props;

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      if (!disabled) {
        setAnchorEl(event.currentTarget);
      }
    },
    [setAnchorEl, disabled]
  );

  const handleClose = useCallback(() => {
    setAnchorEl(null);
  }, [setAnchorEl]);

  const open = useMemo(() => Boolean(anchorEl), [anchorEl]);

  const id = useMemo(
    () =>
      open
        ? `pdf-config-popover-${users.map((user) => user.id).join('-')}`
        : undefined,
    [open, users]
  );

  return (
    <>
      {children({
        ariaDescribeBy: id,
        onClick: handleClick,
        disabled,
      })}
      <Popover
        id={id}
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          padding: 2,
          borderRadius: 1,
        }}
      >
        <DownloadPdfForm
          organization={organization}
          users={users}
          onFinish={handleClose}
          config={config}
        />
      </Popover>
    </>
  );
};
